import React, { useEffect, useState } from 'react';
import { Table, Button, Form, Modal, Badge, Pagination, Spinner, Row, Col, Alert } from 'react-bootstrap';
import { FaPlus, FaEdit, FaTrash, FaCoins } from 'react-icons/fa';
import { adminAPI, creditAPI } from '../services/api';
import { toast } from 'react-toastify';

const Admins = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', credits: 0 });
  const [creditData, setCreditData] = useState({ amount: 0, transaction_method: 'manual', notes: '' });

  useEffect(() => { fetchAdmins(); }, [page, search]);

  const fetchAdmins = async () => {
    try {
      const response = await adminAPI.getAdmins({ page, search });
      setAdmins(response.data.data);
      setTotalPages(response.data.pagination.pages);
    } catch (error) { toast.error('Failed to load admins'); }
    finally { setLoading(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await adminAPI.createAdmin(formData);
      toast.success('Admin created successfully');
      setShowModal(false);
      fetchAdmins();
      setFormData({ name: '', email: '', phone: '', credits: 0 });
    } catch (error) { toast.error(error.response?.data?.message || 'Failed to create admin'); }
  };

  const handleAddCredits = async (e) => {
    e.preventDefault();
    try {
      await creditAPI.addCredits({ user_id: selectedAdmin.id, amount: parseInt(creditData.amount), transaction_method: creditData.transaction_method, notes: creditData.notes });
      toast.success('Credits added successfully');
      setShowCreditModal(false);
      fetchAdmins();
    } catch (error) { toast.error('Failed to add credits'); }
  };

  const getStatusBadge = (status) => <Badge bg={status === 'active' ? 'success' : status === 'disabled' ? 'secondary' : 'warning'}>{status}</Badge>;

  if (loading) return <Spinner animation="border" />;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3>Admins</h3>
        <Button variant="primary" onClick={() => setShowModal(true)}><FaPlus /> Add Admin</Button>
      </div>

      <Form.Control type="text" placeholder="Search admins..." value={search} onChange={(e) => setSearch(e.target.value)} className="mb-3" />

      <Table striped bordered hover responsive>
        <thead><tr><th>Name</th><th>Email</th><th>Phone</th><th>Credits</th><th>Status</th><th>Actions</th></tr></thead>
        <tbody>
          {admins.map((admin) => (
            <tr key={admin.id}>
              <td>{admin.name}</td><td>{admin.email}</td><td>{admin.phone}</td>
              <td><Badge bg="info">{admin.credits}</Badge></td>
              <td>{getStatusBadge(admin.status)}</td>
              <td className="table-actions">
                <Button variant="success" size="sm" onClick={() => { setSelectedAdmin(admin); setShowCreditModal(true); }}><FaCoins /></Button>
                <Button variant="info" size="sm"><FaEdit /></Button>
                <Button variant="danger" size="sm"><FaTrash /></Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <Pagination>{[...Array(totalPages)].map((_, i) => (
        <Pagination.Item key={i + 1} active={i + 1 === page} onClick={() => setPage(i + 1)}>{i + 1}</Pagination.Item>
      ))}</Pagination>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton><Modal.Title>Create New Admin</Modal.Title></Modal.Header>
        <Form onSubmit={handleCreate}>
          <Modal.Body>
            <Row>
              <Col md={6}><Form.Group className="mb-3"><Form.Label>Name</Form.Label><Form.Control required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} /></Form.Group></Col>
              <Col md={6}><Form.Group className="mb-3"><Form.Label>Email</Form.Label><Form.Control type="email" required value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} /></Form.Group></Col>
            </Row>
            <Row>
              <Col md={6}><Form.Group className="mb-3"><Form.Label>Phone</Form.Label><Form.Control value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} /></Form.Group></Col>
              <Col md={6}><Form.Group className="mb-3"><Form.Label>Starting Credits</Form.Label><Form.Control type="number" min="0" value={formData.credits} onChange={(e) => setFormData({...formData, credits: parseInt(e.target.value)})} /></Form.Group></Col>
            </Row>
            <Alert variant="info">Credentials will be auto-generated and emailed to the admin.</Alert>
          </Modal.Body>
          <Modal.Footer><Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button><Button variant="primary" type="submit">Create Admin</Button></Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showCreditModal} onHide={() => setShowCreditModal(false)}>
        <Modal.Header closeButton><Modal.Title>Add Credits - {selectedAdmin?.name}</Modal.Title></Modal.Header>
        <Form onSubmit={handleAddCredits}>
          <Modal.Body>
            <Form.Group className="mb-3"><Form.Label>Amount</Form.Label><Form.Control type="number" min="1" required value={creditData.amount} onChange={(e) => setCreditData({...creditData, amount: e.target.value})} /></Form.Group>
            <Form.Group className="mb-3"><Form.Label>Transaction Method</Form.Label><Form.Select value={creditData.transaction_method} onChange={(e) => setCreditData({...creditData, transaction_method: e.target.value})}><option value="manual">Manual</option><option value="bank_transfer">Bank Transfer</option><option value="paypal">PayPal</option></Form.Select></Form.Group>
            <Form.Group className="mb-3"><Form.Label>Notes</Form.Label><Form.Control as="textarea" rows={2} value={creditData.notes} onChange={(e) => setCreditData({...creditData, notes: e.target.value})} /></Form.Group>
          </Modal.Body>
          <Modal.Footer><Button variant="secondary" onClick={() => setShowCreditModal(false)}>Cancel</Button><Button variant="success" type="submit">Add Credits</Button></Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default Admins;
